import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { ShowField } from "../../components/ViewApplication/ViewApplication";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel3 } from "../../hoc/isLevel3";
import {
    useAllFields,
    useApplicationFormSteps,
    useApplicationLogs,
} from "../../hooks/applications.hooks";
import { useApplicationForLevel3, useApplicationOverride, useLevel3RevokeApplication } from "../../hooks/level-3-application-approval.hooks";
import { RevokeApplicationModal } from "../../components/CommonComponents/RevokeApplicationModal/RevokeApplicationModal";
import { RevocationDisclaimer } from "../../components/CommonComponents/RevocationDisclaimer/RevocationDisclaimer";
import { formatDate } from "../../utils/date";
import html2pdf from 'html2pdf.js';
import { useToggle } from "../../hooks/common";
import { RenderAllLogs } from "../../pages/LevelTwo/RenderLogs";

const LevelThreeApplicationDetail = isLevel3(() => {
    const location = useLocation();
    const navigate = useNavigate();
    const id = location.state?.id;
    const [logsDialog, toggleLogsDialog] = useToggle();
    const allLogsQuery = useApplicationLogs({ id: +id });
    const revokeMutation = useLevel3RevokeApplication();
    const [revokeModalOpen, setRevokeModalOpen] = useState(false);

    const contentRef = useRef(null);
    // Ref to the content to be captured for the PDF
    const handleDownloadPDF = () => {
        // Get the element to generate the PDF from
        const content = contentRef.current;
        // Set PDF options
        const options = {
            margin: 1,
            filename: 'page.pdf',
            image: {
                type: 'jpeg',
                quality: 0.98
            },
            html2canvas: {
                scale: 2
            },
            jsPDF: {
                unit: 'in',
                format: 'letter',
                orientation: 'portrait'
            }
        };
        // const updatedData = `<div><p>Application Id</p>${content}</div>`
        // Generate and download the PDF
        html2pdf().from(content).set(options).save();
    };

    const {
        isError: isError1,
        isFetching: isFetching1,
        data: application,
    } = useApplicationForLevel3({ id: String(id) });
    const {
        data: result,
        isError: isError2,
        isFetching: isFetching2,
    } = useApplicationFormSteps(Number(application?.name_of_service));

    const {
        isFetching: isFetching3,
        isError: isError3,
        data: allFields,
    } = useAllFields({
        serviceName: String(application?.name_of_service || ""),
    });


    const {
        data: overrideData
    } = useApplicationOverride(application?.applicationId);

    const steps = result?.data || [];
    const isFetching = isFetching1 || isFetching2;

    const selectedFields = useMemo(
        () =>
            (steps || [])
                .filter((e) => !["Documents", "NOC"].includes(e.type))
                .flatMap((e) => e.fields || [])
                .reduce<Map<string, true>>((acc, e) => acc.set(e.uid, true), new Map()),
        [steps]
    );

    const uniqueFields = useMemo(() => {
        const seen = new Set();
        return (allFields || []).filter((e) => {
            if (!e.uid) return true;
            if (seen.has(e.uid)) return false;
            seen.add(e.uid);
            return true;
        });
    }, [allFields]);

    const isError =
        isError1 || isError2 || (!isFetching && (!application || !steps?.length));

    const setStuff = useSetStuffToRenderOnNavbar();

    useEffect(() => {
        setStuff(
            <div className="md:ml-auto md:mr-8">
                <p className="text-grey-600">
                    <span className="w-28 font-semibold inline-block">
                        Application ID
                    </span>
                    <span className="text-grey-600 font-semibold mx-2">:</span>
                    <span>{application?.applicationId || "Nil"}</span>
                </p>
                <p className="text-grey-600 flex items-center">
                    <span className="w-28 text-grey-600 font-semibold inline-block">
                        Status
                    </span>
                    <span className="text-grey-600 font-semibold mx-2">:</span>
                    <span className="text-primary">{application?.status || "Nil"}</span>
                </p>
            </div>
        );
        return () => setStuff(null);
    }, [setStuff, application]);

    if (isNaN(+String(id))) {
        return <Navigate to={"/user-applications"} replace />;
    }

    if (isFetching || isError) {
        return (
            <div className="flex justify-center items-center p-8">
                {isError ? (
                    <p>Something went wrong while fetching info!</p>
                ) : (
                    <SpinnerCircular />
                )}
            </div>
        );
    }

    console.log("🔥 [L3] Full application data from BE:", application);
    console.log(
        "🧾 submitter fields:",
        {
            type_of_application_submitter:
                application?.typeOfApplicationSubmitter,
            submitted_by: application?.submittedBy,
        }
    );

    const normalizedApplication = {
        ...application,
        // map camelCase → snake_case
        type_of_application_submitter:
            application?.type_of_application_submitter ??
            application?.typeOfApplicationSubmitter,

        submitted_by:
            application?.submitted_by ??
            application?.submittedBy,
    };

    const PrintData = () => {
        return <div style={{ display: 'none' }}>
            <div ref={contentRef}>
                {overrideData?.data ?
                    <div>

                            <h4 className="text-center text-grey-600 font-semibold mb-8 text-xl sm:text-2xl lg:text-2xl mr-2">
                                Application Details
                            </h4>
                            <div className="flex justify-end">
                                <div className="bg-blue-500 rounded-lg px-3 py-3 max-w-sm print:bg-blue-500" style={{ WebkitPrintColorAdjust: "exact" }}>                                <p className="text-white">Overridden by: {overrideData?.data?.override_by}</p>
                                <p className="text-white">Date: {formatDate(new Date(overrideData?.data?.updated_date))}</p>

                            </div>
                        </div>
                        <h5 className="py-5 font-semibold">Override Reason</h5>
                        <p className="pb-7">{overrideData?.data?.reason}</p>
                    </div>
                    : <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
                        Application Details
                    </h4>}
                <div className="flex flex-col ml-auto mr-8 justify-items-end items-end">
                    <p className="text-grey-600">
                        <span className="w-28 font-semibold inline-block">
                            Application ID
                        </span>
                        <span className="text-grey-600 font-semibold mx-2">:</span>
                        <span>{application?.applicationId || "Nil"}</span>
                    </p>
                    <p className="text-grey-600 flex items-center">
                        <span className="text-grey-600 font-semibold inline-block">
                            Status
                        </span>
                        <span className="text-grey-600 font-semibold mx-2">:</span>
                        <span className="text-primary">{application?.status || "Nil"}</span>
                    </p>
                </div>
                <ApplicationList />
            </div>
        </div>
    }


    const ApplicationList = () => {
        let i = 0;
        return <div>
            {uniqueFields
                .filter((e) => selectedFields.has(e.uid) || !!application?.[e.uid])
                .map((e) => (
                    <ShowField
                        field={e}
                        index={++i}
                        key={`field-${e.id}`}
                        data={normalizedApplication || {}}
                    />
                ))}
        </div>
    }

    if (logsDialog) {
        return (
            <div className="px-4 pb-4 md:p-4">
                <div className="bg-white p-3 md:p-8">
                    <h4 className="text-center text-grey-600 font-semibold mb-4 text-2xl">
                        Audit History
                    </h4>
                    <div className="flex justify-start mb-4 gap-6">
                        <button
                            type="button"
                            className="text-base font-bold disabled:text-gray-500"
                            onClick={toggleLogsDialog}
                        >
                            <i className="fa fa-arrow-left"></i>
                            Back
                        </button>
                    </div>
                    <RenderAllLogs
                        onClose={toggleLogsDialog}
                        logs={allLogsQuery.data ?? []}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 pb-4 md:p-4">
            <div className="bg-white p-4 sm:p-8 rounded-xl shadow-lg border border-gray-300">
                {overrideData?.data ?
                    <div>

                            <h4 className="text-center text-grey-600 font-semibold mb-8 text-xl sm:text-2xl lg:text-2xl mr-2">
                                Application Details
                            </h4>
                            <div className="flex justify-end">
                            <div className=" bg-blue-500 rounded-lg p-2">
                                <p className="text-white">Overridden by: {overrideData?.data?.override_by}</p>
                                <p className="text-white">Date: {formatDate(new Date(overrideData?.data?.updated_date))}</p>
                            </div>
                            </div>

                        <h5 className="py-5 font-semibold">Override Reason</h5>
                        <p className="pb-7">{overrideData?.data?.reason}</p>
                    </div>
                    : <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
                        Application Details
                    </h4>}
                <RevocationDisclaimer
                    status={application?.status}
                    revocationCategory={application?.revocationCategory}
                    revocationReason={application?.revocationReason}
                    revokedBy={application?.revokedBy}
                    revocationDate={application?.revocationDate}
                />
                <div className="flex justify-end mb-4 gap-4 items-center flex-wrap">
                    {application?.status && !application.status.startsWith("Revoked") && application.status !== "Draft" && (
                        <button
                            type="button"
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-1.5 px-4 rounded-lg shadow-sm text-sm transition"
                            onClick={() => setRevokeModalOpen(true)}
                        >
                            Revoke Application
                        </button>
                    )}
                    {!!allLogsQuery.data?.length && (
                        <button
                            type="button"
                            className="text-base font-bold disabled:text-gray-500"
                            onClick={toggleLogsDialog}
                        >
                            Audit History
                        </button>
                    )}
                    <button
                        className="text-blue-600 underline"
                        onClick={() =>
                            window.open("/blocked-plots-viewer", "_blank")
                        }
                    >
                        List of Blocked Plots
                    </button>
                </div>
                <ApplicationList />
                <PrintData />
                <div className="d-flex gap-5 justify-center items-center mt-4">

                <RevokeApplicationModal
                    isOpen={revokeModalOpen}
                    onClose={() => setRevokeModalOpen(false)}
                    applicationId={application?.applicationId}
                    applicantName={application?.plotOwnerSName ? String(application.plotOwnerSName) : undefined}
                    isPending={revokeMutation.isPending}
                    onConfirm={({ reason, reasonCategory }) => {
                        if (!application) return;
                        revokeMutation.mutate(
                            {
                                applicationId: application.id,
                                application_id: application.applicationId,
                                user_id: application.userId,
                                reason,
                                reasonCategory,
                            },
                            {
                                onSuccess: () => {
                                    setRevokeModalOpen(false);
                                    window.location.reload();
                                },
                            }
                        );
                    }}
                />
                    <button className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full disabled:bg-gray-300" onClick={() => navigate('/level-three-applications')}>Back</button>

                    <button
                        type="button"
                        onClick={handleDownloadPDF}
                        className="bg-primary px-6 py-2 rounded-full font-bold text-white"
                    >
                        Download
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/level-three-application-doc", { state: location.state })
                        }
                        className="bg-primary px-6 py-2 rounded-full font-bold text-white"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
});

export default LevelThreeApplicationDetail;
